import { createAsyncThunk } from '@reduxjs/toolkit';
import _orderBy from 'lodash/orderBy';
import _flatten from 'lodash/flatten';

import { ARCADE_STRUCTURES } from 'constants/common';

import { updateLessonStatusViaApi } from 'store/helpers/updateLessonStatusViaApi';

import * as api from 'api/methods';

import * as types from './types';

const _getArcadeModules = async (arcadeId: string) => {
  const promises = [
    api.getArcadeModules(arcadeId),
    api.getArcadeLessons(arcadeId),
  ];

  const [modules, lessons] = await Promise.all(promises);

  return _orderBy(modules, ['order'], ['asc']).map((module: any) => ({
    ...module,
    lessons: lessons.filter((lesson: any) => lesson.moduleId === module.id),
  }));
};

export const getArcadeById = createAsyncThunk<any, any>(
  types.GET_CURRENT_ARCADE,
  async (arcadeId: string) => {
    const arcade = await api.getArcadeById(arcadeId);

    if (arcade.structure === ARCADE_STRUCTURES.MODULES) {
      const modules = await _getArcadeModules(arcadeId);

      return {
        ...arcade,
        modules,
      };
    }

    if (arcade.structure === ARCADE_STRUCTURES.LESSONS) {
      const lessons = await api.getArcadeLessons(arcadeId);

      return { ...arcade, lessons };
    }

    return arcade;
  },
);

export const updateArcadeById = createAsyncThunk<any, any>(
  types.UPDATE_ARCADE_BY_ID,
  async ({ arcadeId, data }: { arcadeId: string; data: any }) => {
    const arcade = await api.updateArcadeById({
      arcadeId,
      data,
    });

    if (data.structure && data.structure === ARCADE_STRUCTURES.MODULES) {
      const modules = await _getArcadeModules(arcadeId);

      return { ...arcade, modules };
    }

    if (data.structure === ARCADE_STRUCTURES.LESSONS) {
      const lessons = await api.getArcadeLessons(arcadeId);

      return { ...arcade, lessons };
    }

    return arcade;
  },
);

export const updateArcadeStatus = createAsyncThunk<any, any>(
  types.UPDATE_ARCADE_STATUS,
  async (
    {
      arcadeId,
      withLessons,
      status,
    }: { arcadeId: string; withLessons: any; status: any },
    { getState }: { getState: any },
  ) => {
    if (withLessons) {
      const state = getState() as any;

      let lessons = [];

      if (state.currentArcade.structure === ARCADE_STRUCTURES.MODULES) {
        lessons = _flatten(
          state.currentArcade.modules.map((module: any) => module.lessons),
        );
      } else if (state.currentArcade.structure === ARCADE_STRUCTURES.LESSONS) {
        lessons = state.currentArcade.lessons;
      }

      await Promise.all(
        lessons.map((lesson: any) =>
          updateLessonStatusViaApi({
            lessonId: lesson.id,
            lessonType: lesson.type,
            data: { status },
          }),
        ),
      );
    }

    const arcade = await api.updateArcadeById({
      arcadeId,
      data: { status },
    });

    return {
      ...arcade,
      bulkLessonStatus: withLessons ? status : undefined,
    };
  },
);

export const createArcadeModule = createAsyncThunk<any, any>(
  types.CREATE_ARCADE_MODULE,
  async ({ arcadeId, data }: { arcadeId: string; data: any }) => {
    const module = await api.createArcadeModule({
      arcadeId,
      data,
    });

    return module;
  },
);

export const updateArcadeModuleById = createAsyncThunk<any, any>(
  types.UPDATE_ARCADE_MODULE_BY_ID,
  async ({
    arcadeId,
    moduleId,
    data,
  }: {
    arcadeId: string;
    moduleId: string;
    data: any;
  }) => {
    const module = await api.updateArcadeModuleById({
      arcadeId,
      moduleId,
      data,
    });

    return module;
  },
);

export const removeArcadeModuleById = createAsyncThunk<any, any>(
  types.REMOVE_ARCADE_MODULE_BY_ID,
  async ({ arcadeId, moduleId }: { arcadeId: string; moduleId: string }) => {
    const module = await api.removeArcadeModuleById({ arcadeId, moduleId });

    return module;
  },
);

export const reorderArcadeLessons = createAsyncThunk<any, any>(
  types.REORDER_ARCADE_LESSONS,
  async ({ arcadeId, data }: { arcadeId: string; data: any }) => {
    const lessons = await api.reorderArcadeLessons({ arcadeId, data });

    return { moduleId: data.moduleId, lessons };
  },
);

export const reorderArcadeModules = createAsyncThunk<any, any>(
  types.REORDER_ARCADE_MODULES,
  async ({ arcadeId, data }: { arcadeId: string; data: any }) => {
    const modules = await api.reorderArcadeModules({ arcadeId, data });

    return modules;
  },
);
